import React, { useCallback, useState } from 'react'
import clsx from 'clsx'
import Popover from '@material-ui/core/Popover'
import {
  useChatContext,
  useMessageContext,
  useTranslationContext,
} from 'stream-chat-react'
import MessagingMessageActionsBox from './messaging-message-actions-box'
import { MessagingActionsIcon } from './messaging-message-action-icons'

const isUserMuted = (message, mutes) => {
  if (!mutes || !message) return false

  return mutes.some(mute => mute.target.id === message.user?.id)
}

const shouldShowActions = ({ customMessageActions, messageActions }) => {
  if (customMessageActions && Object.keys(customMessageActions).length > 0) {
    return true
  }

  const actionable = messageActions.filter(
    action => !['react', 'reply'].includes(action),
  )

  return actionable.length > 0
}

const MessagingMessageActions = ({
  ActionsIcon = MessagingActionsIcon,
  customWrapperClass = '',
  getMessageActions: propGetMessageActions,
  handleDelete: propHandleDelete,
  handleFlag: propHandleFlag,
  handleMarkUnread: propHandleMarkUnread,
  handleMute: propHandleMute,
  handlePin: propHandlePin,
  inline,
  mine,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const { mutes } = useChatContext()
  const {
    customMessageActions,
    getMessageActions: contextGetMessageActions,
    handleDelete: contextHandleDelete,
    handleFlag: contextHandleFlag,
    handleMarkUnread: contextHandleMarkUnread,
    handleMute: contextHandleMute,
    handlePin: contextHandlePin,
    isMyMessage,
    message,
    setEditingState,
    threadList,
  } = useMessageContext()
  const { t } = useTranslationContext()

  const getMessageActions = propGetMessageActions || contextGetMessageActions
  const handleDelete = propHandleDelete || contextHandleDelete
  const handleFlag = propHandleFlag || contextHandleFlag
  const handleMarkUnread = propHandleMarkUnread || contextHandleMarkUnread
  const handleMute = propHandleMute || contextHandleMute
  const handlePin = propHandlePin || contextHandlePin
  const isMine = mine ? mine() : isMyMessage()
  const isMuted = useCallback(() => isUserMuted(message, mutes), [
    message,
    mutes,
  ])
  const messageActions = getMessageActions()
  const open = Boolean(anchorEl)

  const handleClose = () => setAnchorEl(null)

  if (
    !shouldShowActions({
      customMessageActions,
      messageActions,
      threadList,
    })
  ) {
    return null
  }

  const wrapperClassName =
    customWrapperClass ||
    clsx(
      'str-chat__message-simple__actions__action',
      'str-chat__message-simple__actions__action--options',
      'str-chat__message-actions-container',
      'messaging-message-actions',
      {
        'messaging-message-actions--open': open,
      },
    )

  const Wrapper = inline ? 'span' : 'div'

  return (
    <Wrapper className={wrapperClassName} data-testid="message-actions">
      <button
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('aria/Open Message Actions Menu')}
        className="str-chat__message-actions-box-button"
        data-testid="message-actions-toggle-button"
        onClick={event => {
          event.stopPropagation()
          setAnchorEl(previous => (previous ? null : event.currentTarget))
        }}
        type="button"
      >
        <ActionsIcon className="str-chat__message-action-icon" />
      </button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: isMine ? 'right' : 'left',
          vertical: 'top',
        }}
        className="messaging-message-actions-popover"
        disableRestoreFocus
        marginThreshold={8}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          horizontal: isMine ? 'right' : 'left',
          vertical: 'bottom',
        }}
        PaperProps={{
          className: 'messaging-message-actions-popover-paper',
        }}
      >
        <MessagingMessageActionsBox
          getMessageActions={getMessageActions}
          handleDelete={handleDelete}
          handleEdit={setEditingState}
          handleFlag={handleFlag}
          handleMarkUnread={handleMarkUnread}
          handleMute={handleMute}
          handlePin={handlePin}
          isUserMuted={isMuted}
          mine={isMine}
          onClose={handleClose}
          open={open}
        />
      </Popover>
    </Wrapper>
  )
}

export default MessagingMessageActions
